import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../../services/api";
import { toast } from "react-toastify";

/**
 * Hook personalizado para gestión de plantillas de email
 * Maneja todas las llamadas API y estados
 */
const useEmailTemplates = () => {
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filterPurpose, setFilterPurpose] = useState("");

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Obtener todas las plantillas
   */
  const fetchTemplates = useCallback(async (purpose = null) => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const url = purpose
        ? `/smtp/templates?purpose=${purpose}`
        : "/smtp/templates";
      const { data } = await api.get(url);
      if (isMountedRef.current) {
        if (data.success) {
          setTemplates(data.data || []);
        } else {
          throw new Error(data.message || "Error obteniendo plantillas");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo plantillas:", err);
        toast.error(errorMessage);
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  /**
   * Obtener plantilla específica
   */
  const fetchTemplate = useCallback(async (id) => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.get(`/smtp/templates/${id}`);
      if (isMountedRef.current) {
        if (data.success) {
          setSelectedTemplate(data.data);
          return data.data;
        } else {
          throw new Error(data.message || "Error obteniendo plantilla");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo plantilla:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, []);

  /**
   * Crear nueva plantilla
   */
  const createTemplate = useCallback(async (templateData) => {
    if (!isMountedRef.current) return null;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.post("/smtp/templates", templateData);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(
            data.message || "Plantilla creada exitosamente"
          );
          await fetchTemplates(filterPurpose || null); // Refrescar lista
          return data.data;
        } else {
          throw new Error(data.message || "Error creando plantilla");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error creando plantilla:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchTemplates, filterPurpose]);

  /**
   * Actualizar plantilla
   */
  const updateTemplate = useCallback(async (id, templateData) => {
    if (!isMountedRef.current) return null;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.put(`/smtp/templates/${id}`, templateData);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(
            data.message || "Plantilla actualizada exitosamente"
          );
          await fetchTemplates(filterPurpose || null); // Refrescar lista
          if (selectedTemplate && selectedTemplate.id === id) {
            await fetchTemplate(id); // Actualizar plantilla seleccionada
          }
          return data.data;
        } else {
          throw new Error(data.message || "Error actualizando plantilla");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error actualizando plantilla:", err);
        toast.error(errorMessage);
      }
      return null;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchTemplates, fetchTemplate, selectedTemplate, filterPurpose]);

  /**
   * Eliminar plantilla (soft delete)
   */
  const deleteTemplate = useCallback(async (id) => {
    if (!isMountedRef.current) return false;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.delete(`/smtp/templates/${id}`);
      if (isMountedRef.current) {
        if (data.success) {
          toast.success(data.message || "Plantilla eliminada exitosamente");
          await fetchTemplates(filterPurpose || null); // Refrescar lista
          if (selectedTemplate && selectedTemplate.id === id) {
            setSelectedTemplate(null);
          }
          return true;
        } else {
          throw new Error(data.message || "Error eliminando plantilla");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage =
          err.response?.data?.message || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error eliminando plantilla:", err);
        toast.error(errorMessage);
      }
      return false;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchTemplates, selectedTemplate, filterPurpose]);

  /**
   * Duplicar plantilla
   */
  const duplicateTemplate = useCallback(async (template) => {
    if (!isMountedRef.current) return null;
    try {
      const newTemplateData = {
        name: `${template.name} (Copia)`,
        description: template.description,
        purpose: template.purpose,
        subject: template.subject,
        htmlContent: template.htmlContent,
        textContent: template.textContent,
        variables: template.variables || [],
        isSystemTemplate: false, // Las copias no son del sistema
        isActive: true
      };
      return await createTemplate(newTemplateData);
    } catch (err) {
      console.error("Error duplicando plantilla:", err);
      return null;
    }
  }, [createTemplate]);

  // Cargar plantillas al montar o cambiar filtro
  useEffect(() => {
    fetchTemplates(filterPurpose || null);
  }, [fetchTemplates, filterPurpose]);

  return {
    loading,
    error,
    templates,
    selectedTemplate,
    filterPurpose,
    fetchTemplates,
    fetchTemplate,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
    setSelectedTemplate,
    setFilterPurpose
  };
};

export default useEmailTemplates;
